import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, X, Plus, Trash2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { useCompare } from "@/hooks/useCompare";
import { feeLabel, yearlyFee } from "@/components/FeeBox";
import { get, money, communityUrl, img, RENTAL_LABEL, AMENITY_OPTIONS } from "@/lib/api";

const ROWS = [
  ["Status", (c) => <StatusBadge status={c.status} />],
  ["Location", (c) => `${c.city}, ${c.state_code}`],
  ["HOA fee", (c) => <span><span className="block text-[10px] uppercase tracking-wider text-slate-500">{feeLabel(c)}</span><span className="font-display text-xl font-bold text-[#0B2150]">{c.fee_estimated ? money(c.monthly_fee) : c.fee_range || money(c.monthly_fee)}</span></span>],
  ["Est. yearly fee", (c) => (yearlyFee(c) != null ? `${money(yearlyFee(c))}/yr` : "Not reported")],
  ["Fee frequency", (c) => c.fee_frequency],
  ["Transfer fee", (c) => (c.transfer_fee == null ? "Not reported" : money(c.transfer_fee))],
  ["Community type", (c) => c.community_type],
  ["Pets allowed", (c) => (c.pets_allowed == null ? "Not reported" : c.pets_allowed ? "Yes" : "No")],
  ["Pet policy", (c) => c.pet_policy],
  ["Rental policy", (c) => c.rental_notes || RENTAL_LABEL[c.rental_policy]],
  ["Parking", (c) => c.parking_rules],
  ["Architectural", (c) => c.architectural_rules],
];

function AddBox() {
  const [q, setQ] = useState("");
  const { toggle, has } = useCompare();
  const { data } = useQuery({ queryKey: ["compare-search", q], queryFn: () => get("/communities", { q, limit: 6 }), enabled: q.length >= 2 });
  return (
    <div className="rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-5" data-testid="compare-add-box">
      <label htmlFor="compare-search" className="text-sm font-semibold text-[#0B2150]">Add a community</label>
      <input id="compare-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or city" data-testid="compare-search-input"
        className="mt-2 w-full rounded-full border border-[#CBD5E1] px-4 py-2 text-sm" />
      <ul className="mt-3 space-y-1">
        {data?.items?.map((c) => (
          <li key={c.id}>
            <button onClick={() => toggle(c)} disabled={has(c.id)} data-testid={`compare-search-result-${c.id}`}
              className="w-full flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm hover:bg-[#E6F4F1] disabled:opacity-50">
              <span><span className="font-medium text-[#0B2150]">{c.name}</span> <span className="text-slate-500">{c.city}, {c.state_code}</span></span>
              <Plus className="h-4 w-4 text-[#027A6D]" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Compare() {
  const { items, remove, clear } = useCompare();
  const ids = items.map((i) => i.id).join(",");
  const { data = [] } = useQuery({ queryKey: ["compare", ids], queryFn: () => get("/communities/by-ids", { ids }), enabled: !!ids });
  const amenities = [...new Set([...AMENITY_OPTIONS, ...data.flatMap((c) => c.amenities || [])])];

  return (
    <div data-testid="compare-page">
      <Seo title="Compare HOA Communities" description="Compare HOA fees, pet rules, rental restrictions and amenities side by side." path="/compare" />
      <PageHeader eyebrow="Compare" title="Compare communities" subtitle="Place up to three HOAs side by side. Add communities from any listing or search below." />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 space-y-5 sm:space-y-8">
        {items.length < 3 && <div className="max-w-md"><AddBox /></div>}
        {data.length === 0 ? (
          <p className="text-slate-600" data-testid="compare-empty">No communities selected yet. <Link to="/search" className="text-[#027A6D] underline">Browse the directory</Link> and click “Compare”.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[#E2E8F0] bg-white" data-testid="compare-view-matrix">
            <table className="w-full min-w-[720px] text-sm">
              <caption className="sr-only">Side-by-side HOA comparison</caption>
              <thead>
                <tr>
                  <th scope="col" className="w-48 p-4 text-left align-bottom"><button onClick={clear} className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-red-600" data-testid="compare-clear-btn"><Trash2 className="h-3.5 w-3.5" />Clear all</button></th>
                  {data.map((c) => (
                    <th key={c.id} scope="col" className="p-4 text-left align-top">
                      <img src={img(c.image)} alt="" className="h-28 w-full object-cover rounded-xl" />
                      <div className="mt-3 flex items-start justify-between gap-2">
                        <Link to={communityUrl(c)} className="font-semibold text-[#0B2150] hover:underline">{c.name}</Link>
                        <button onClick={() => remove(c.id)} aria-label={`Remove ${c.name}`} data-testid={`compare-remove-${c.id}`} className="p-1 rounded hover:bg-slate-100"><X className="h-4 w-4" /></button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROWS.map(([label, fn]) => (
                  <tr key={label} className="border-t border-[#E2E8F0]">
                    <th scope="row" className="p-4 text-left font-semibold text-[#334155] bg-[#F8FAFC]">{label}</th>
                    {data.map((c) => <td key={c.id} className="p-4 text-[#0F172A]">{fn(c) || "Not reported"}</td>)}
                  </tr>
                ))}
                {amenities.map((a) => (
                  <tr key={a} className="border-t border-[#E2E8F0]">
                    <th scope="row" className="p-4 text-left font-medium text-[#334155] bg-[#F8FAFC]">{a}</th>
                    {data.map((c) => <td key={c.id} className="p-4">{c.amenities?.includes(a) ? <Check className="h-5 w-5 text-[#03A896]" aria-label="Yes" /> : <X className="h-5 w-5 text-slate-300" aria-label="No" />}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
